import CellValue from '@app/shared/components/cell-value/CellValue';
import { l } from '@locale/setupI18n';
import { FC, useMemo } from 'react';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import {
  amountRules,
  decimalCalculatedViewRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { InstrumentType, TransactionType } from '@app/shared/models/contracts/enums/shared-enums';
import { enumKeyByValue } from '@app/shared/helpers';
import { ValuationMovement } from './ValuationMovement';
import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import { Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters/number-value-formatter';
import {
  getWaterfallCaseYearInstrumentTransactionSummary,
  getWaterfallCaseYearInstrumentValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import { useAppSelector } from '@app/core/hooks/redux-hooks';
import classNames from 'classnames';
import styles from '../../components/table-section-values.module.scss';
import { CashFlowType, useTotalPvModal } from '../pwerm-waterfall-utils/useTotalPvModal';
import { TotalPvOfInterimCashFlowsModal } from './TotalPvOfInterimCashFlowsModal';
import { ForecastDto, CaseDto } from '@app/shared/models/contracts/project-dto';
import { maxNumberOfTransactionsInYear } from './utils/transactionUtils';

const resolveDiscountFactorRowLabel = (
  transactionTypeSpecificLabel: string,
  multipleTransactions: boolean
) => (multipleTransactions ? transactionTypeSpecificLabel : l('_DiscountFactor'));

export const InstrumentOtherInterimCashFlows: FC<{
  instrument: InstrumentDefinitionWithId;
  hasInfusions: boolean;
  redemptionTransactions: Record<
    string,
    Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
  >;
  dividendTransactions: Record<
    string,
    Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
  >;
  cashInterestTransactions: Record<
    string,
    Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
  >;
  columnsCount: number;
}> = ({
  instrument,
  hasInfusions,
  redemptionTransactions,
  dividendTransactions,
  cashInterestTransactions,
  columnsCount,
}) => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const maxDataColspanCount = columnsCount - 1;
  const isOrdinaryEquity =
    instrument.type === enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity);
  const modalHandler = useTotalPvModal(CashFlowType.Other, results, hasInfusions, isOrdinaryEquity);
  const redemptionLabel = isOrdinaryEquity ? l('_Buyback') : l('_Redemption');

  const shouldDisplayDloc = useMemo(() => {
    if (!results?.waterfall?.cases) {
      return false;
    }

    return Object.values(results.waterfall.cases).some((caseItem) =>
      Object.values(caseItem.years || {}).some((year) => {
        const instrumentData = year.instruments?.[instrument.instrumentId];
        if (!instrumentData) {
          return false;
        }
        return instrumentData.dlocPc != null && instrumentData.dlocPc !== 0;
      })
    );
  }, [results, instrument.instrumentId]);

  const totalTransactionRowCount =
    maxNumberOfTransactionsInYear(redemptionTransactions) +
    maxNumberOfTransactionsInYear(dividendTransactions) +
    maxNumberOfTransactionsInYear(cashInterestTransactions);
  const multipleTransactions = totalTransactionRowCount > 1;

  const getTotalPvCell = (forecast: ForecastDto, caseItem: CaseDto) => {
    const totalPvValue = getWaterfallCaseYearInstrumentTransactionSummary(
      results,
      caseItem.caseId,
      forecast.forecastId,
      instrument.instrumentId,
      (transactionSummary) => transactionSummary.totalPvOfOtherMovement
    );
    const isClickable = Boolean(totalPvValue);
    const handleClick = () => {
      if (isClickable) {
        modalHandler.handleCellClick(caseItem.caseId, forecast.forecastId, instrument);
      }
    };
    return (
      <div onClick={isClickable ? handleClick : undefined}>
        <CellValue
          {...amountRules}
          className={classNames('table-primary__cell--strong', {
            [styles['clickable-cell']]: isClickable,
          })}
          value={totalPvValue}
          dataTestId={`total-pv-interim-cell-${caseItem.caseId}-${forecast.forecastId}`}
        />
      </div>
    );
  };

  return (
    <>
      <tr>
        <th className="table-primary__cell--strong">
          {hasInfusions ? l('_OtherInterimCashFlows') : l('_InterimCashFlows')}
        </th>
        <th className="table-primary__cell--strong" colSpan={maxDataColspanCount} />
      </tr>
      <ValuationMovement
        instrument={instrument}
        transactionsByCaseAndYear={redemptionTransactions}
        transactionType={TransactionType.Redemption}
        rowLabel={redemptionLabel}
        valueSelector={(transaction) => transaction?.amount}
        cellValueFormattingRules={amountRules}
      />
      <ValuationMovement
        instrument={instrument}
        transactionsByCaseAndYear={dividendTransactions}
        transactionType={TransactionType.Dividend}
        rowLabel={l('_Dividends')}
        valueSelector={(transaction) => transaction?.amount}
        cellValueFormattingRules={amountRules}
      />
      <ValuationMovement
        instrument={instrument}
        transactionsByCaseAndYear={cashInterestTransactions}
        transactionType={TransactionType.CashPaid}
        rowLabel={l('_CashInterest')}
        valueSelector={(transaction) => transaction?.amount}
        cellValueFormattingRules={amountRules}
      />
      {multipleTransactions && (
        <TableSectionValuesPerForecast
          title={hasInfusions ? l('_TotalOtherInterimCashFlows') : l('_TotalInterimCashFlows')}
          additionalTitleClassNames="table-primary__cell--strong"
          renderSpacer={false}
          renderCell={({ forecast, caseItem }) => (
            <CellValue
              {...amountRules}
              className="table-primary__cell--strong"
              value={getWaterfallCaseYearInstrumentTransactionSummary(
                results,
                caseItem.caseId,
                forecast.forecastId,
                instrument.instrumentId,
                (transactionSummary) => transactionSummary.totalCashFlowMovementOfOtherTransactions,
                true
              )}
              dataTestId={`total-other-interim-cashflows-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
        />
      )}
      <TableSectionValuesPerForecast
        title={l('_CostOfEquity')}
        renderSpacer={false}
        renderCell={({ forecast, caseItem }) => {
          const value = getCalculatedProjectValueInPercent(
            getWaterfallCaseYearInstrumentTransactionSummary(
              results,
              caseItem.caseId,
              forecast.forecastId,
              instrument.instrumentId,
              (transactionSummary) =>
                transactionSummary.otherInterimTransactionsDiscountRate ??
                transactionSummary.originalInterimTransactionsDiscountRate
            )
          );
          return (
            <CellValue
              {...enteredPercentageViewRules}
              value={value}
              dataTestId={`discount-rate-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          );
        }}
      />
      {multipleTransactions && (
        <TableSectionValuesPerForecast
          title={l('_DiscountFactor')}
          renderSpacer={false}
          additionalTitleClassNames="table-primary__cell--strong"
          renderCell={() => <CellValue value={' '} />}
        />
      )}
      <ValuationMovement
        instrument={instrument}
        transactionsByCaseAndYear={redemptionTransactions}
        transactionType={TransactionType.Redemption}
        rowLabel={resolveDiscountFactorRowLabel(redemptionLabel, multipleTransactions)}
        valueSelector={(transaction) => transaction?.discountFactor}
        cellValueFormattingRules={decimalCalculatedViewRules}
      />
      <ValuationMovement
        instrument={instrument}
        transactionsByCaseAndYear={dividendTransactions}
        transactionType={TransactionType.Dividend}
        rowLabel={resolveDiscountFactorRowLabel(l('_Dividends'), multipleTransactions)}
        valueSelector={(transaction) => transaction?.discountFactor}
        cellValueFormattingRules={decimalCalculatedViewRules}
      />
      <ValuationMovement
        instrument={instrument}
        transactionsByCaseAndYear={cashInterestTransactions}
        transactionType={TransactionType.CashPaid}
        rowLabel={resolveDiscountFactorRowLabel(l('_CashInterest'), multipleTransactions)}
        valueSelector={(transaction) => transaction?.discountFactor}
        cellValueFormattingRules={decimalCalculatedViewRules}
      />
      <TableSectionValuesPerForecast
        title={
          hasInfusions ? l('_TotalPvOfOtherInterimCashFlows') : l('_TotalPvOfInterimCashFlows')
        }
        additionalTitleClassNames="table-primary__cell--strong"
        rowTooltipInfo={l('_InterimCashFlowMovementTooltip')}
        renderCell={({ forecast, caseItem }) => getTotalPvCell(forecast, caseItem)}
      />
      <TotalPvOfInterimCashFlowsModal
        isOpen={modalHandler.isOpen}
        onClose={modalHandler.closeModal}
        transactions={modalHandler.transactions}
        getTransactionTypeLabel={modalHandler.getTransactionTypeLabel}
        isTransactionCashPaidOrDividend={modalHandler.isTransactionCashPaidOrDividend}
        tableTitle={modalHandler.tableTitle}
        totalPvValues={modalHandler.totalPvValues}
      />
      <tr>
        <th className="table-primary__cell--strong">{l('_MinorityDiscounts')}</th>
        <th className="table-primary__cell--strong" colSpan={maxDataColspanCount} />
      </tr>
      {shouldDisplayDloc && (
        <TableSectionValuesPerForecast
          title={l('_DLOC')}
          renderSpacer={false}
          renderCell={({ forecast, caseItem }) => (
            <CellValue
              {...enteredPercentageViewRules}
              value={getCalculatedProjectValueInPercent(
                getWaterfallCaseYearInstrumentValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  instrument.instrumentId,
                  (instrument) => instrument.dlocPc
                )
              )}
              dataTestId={`dloc-cell-${caseItem.caseId}-${forecast.forecastId}`}
            />
          )}
        />
      )}
      <TableSectionValuesPerForecast
        title={l('_DLOM')}
        renderSpacer={false}
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...enteredPercentageViewRules}
            value={getCalculatedProjectValueInPercent(
              getWaterfallCaseYearInstrumentValue(
                results,
                caseItem.caseId,
                forecast.forecastId,
                instrument.instrumentId,
                (instrument) => instrument.otherInterimCashFlowDlomPc
              ) ?? 0
            )}
            dataTestId={`dlom-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerForecast
        title={
          hasInfusions
            ? l('_PostDiscountPvOfOtherInterimCashFlows')
            : l('_PostDiscountPVOfInterimCashFlows')
        }
        renderSpacer={false}
        additionalTitleClassNames="table-primary__cell--strong"
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...amountRules}
            className="table-primary__cell--strong"
            value={getWaterfallCaseYearInstrumentTransactionSummary(
              results,
              caseItem.caseId,
              forecast.forecastId,
              instrument.instrumentId,
              (transactionSummary) => transactionSummary.postDiscountPvOfOtherMovement,
              true
            )}
            dataTestId={`post-discount-pv-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
    </>
  );
};
